import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Check } from "lucide-react";
import { Logo, PebbleMark } from "../components/Logo";
import { EventInquiry } from "../components/landing/EventInquiry";
import { Faq } from "../components/landing/BookClose";
import { VENUES, HUB_BRAND } from "../data/venues";
import { getContent } from "../lib/api";

export default function VenuePage({ fixedSlug }) {
  const { slug } = useParams();
  const venue = VENUES.find((v) => v.slug === (fixedSlug || slug)) || VENUES[0];
  const [content, setContent] = useState(null);

  useEffect(() => {
    getContent().then(setContent).catch(() => {});
  }, []);

  const property = content?.property || {
    phone_tel: HUB_BRAND.phone_tel,
    phone_display: HUB_BRAND.phone_display,
  };
  const showHubLink = !fixedSlug;

  return (
    <div className="bg-cream min-h-screen" data-testid={`venue-page-${venue.slug}`}>
      <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-charcoal/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            {showHubLink && (
              <Link to="/weddings" className="inline-flex items-center gap-1.5 text-sm font-semibold text-soft hover:text-charcoal transition-colors" data-testid="venue-back-link">
                <ArrowLeft size={15} aria-hidden /> All venues
              </Link>
            )}
            <a href={`tel:${property.phone_tel}`} className="inline-flex items-center gap-2 bg-copper text-cream text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-copperdeep transition-colors" data-testid="venue-call-button">
              <Phone size={14} aria-hidden /> <span className="hidden sm:inline">{property.phone_display}</span><span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </header>

      <section className="relative h-[52vh] min-h-[360px] overflow-hidden" aria-label={`${venue.name} hero`}>
        {venue.hero ? (
          <img src={venue.hero.url} alt={venue.hero.alt} className="absolute inset-0 w-full h-full object-cover" data-testid="venue-hero-image" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-sagedeep to-copperdeep flex items-center justify-center" data-testid="venue-hero-placeholder">
            <PebbleMark size={72} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/25 to-transparent" aria-hidden />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-10 w-full">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="text-cream/85 uppercase tracking-[0.26em] text-xs font-semibold mb-2">{venue.location}</p>
              <h1 className="font-serif text-cream text-4xl sm:text-6xl font-semibold" data-testid="venue-headline">Weddings at {venue.name}</h1>
              <p className="text-cream/90 text-lg mt-3 max-w-xl">{venue.tagline}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {venue.photos.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14" aria-label="Celebration gallery" data-testid="venue-gallery">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {venue.photos.map((ph, i) => (
              <motion.div key={ph.url}
                initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="overflow-hidden rounded-2xl shadow-sm aspect-[4/3]">
                <img src={ph.url} alt={ph.alt} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" data-testid={`venue-photo-${i}`} />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-6" aria-label="Event terms">
        <h2 className="font-sans text-2xl font-extrabold tracking-tight uppercase text-charcoal mb-5">Good to know</h2>
        <ul className="space-y-3">
          {venue.details.map((d, i) => (
            <li key={i} className="flex gap-3 text-charcoal/90 leading-relaxed bg-white border border-charcoal/10 rounded-2xl px-5 py-4">
              <span className="mt-1 p-1 rounded-full bg-sage/20 shrink-0"><Check size={14} className="text-sagedeep" aria-hidden /></span>
              {d}
            </li>
          ))}
        </ul>
        {venue.site && venue.site.startsWith("http") && (
          <a href={venue.site} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 mt-6 text-copperdeep font-semibold underline underline-offset-4" data-testid="venue-external-link">
            Full venue details at grandview.elitestrhostingco.com
          </a>
        )}
      </section>

      {venue.faqs?.length > 0 && (
        <Faq faqs={venue.faqs} heading="Wedding questions, answered" sub={`Anything else on your mind — call or text ${property.phone_display}.`} />
      )}

      {!venue.comingSoon ? (
        <EventInquiry property={property} venue={venue.name} />
      ) : (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-20 text-center">
          <p className="text-soft text-lg">Want to hear the moment this venue opens? Call or text <a href={`tel:${property.phone_tel}`} className="font-semibold text-copperdeep underline underline-offset-2">{property.phone_display}</a>.</p>
        </section>
      )}
    </div>
  );
}
